import React from 'react';
import { Loader2, Plus, Trash2, Check } from 'lucide-react';
import { planningApi } from '../../lib/api';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';

/**
 * Éditeur des codes d'absence (CP, RTT, MAL...). Vit ici, dans le module Planning, parce que c'est là
 * qu'on s'en sert. Historique : ajouté aux réglages du Planning le 2026-07-23, déplacé deux jours plus
 * tard vers Paramètres (barre latérale) lors de la centralisation des réglages, puis ramené ici le
 * 2026-08-25 à la demande d'Emeni — la logique du projet est revenue, depuis le 21 août, à ranger chaque
 * réglage dans son module (RDV Pro et Pointage ont fait le même trajet).
 *
 * L'écran Paramètres le garde en secours pour les clients qui n'ont PAS le module Planning : deux d'entre
 * eux éditent ces codes sans avoir de planning (l'un n'a que Planning RDV, l'autre que les demandes de
 * congés). Sans ce repli, ils perdraient purement et simplement l'écran. Un même client ne le voit donc
 * jamais aux deux endroits.
 *
 * `onSaved` : les autres onglets des réglages Planning renvoient un payload COMBINÉ qui contient
 * absenceCodes. Sans cette remontée, modifier un code ici puis enregistrer un autre onglet réécrirait
 * l'ancienne valeur par-dessus.
 */
export interface AbsenceCode { code: string; isFullDay: boolean; autoApply: boolean; color?: string; }
export function AbsenceCodesSettings({ onSaved }: { onSaved?: (codes: AbsenceCode[]) => void }) {
    const [codes, setCodes] = React.useState<AbsenceCode[]>([]);
    const [payload, setPayload] = React.useState<any>({});
    const [newCode, setNewCode] = React.useState('');
    const [newColor, setNewColor] = React.useState('#ffe39b');
    const [loading, setLoading] = React.useState(true);
    const [saving, setSaving] = React.useState(false);
    const [saved, setSaved] = React.useState(false);

    React.useEffect(() => {
        planningApi.getSettings().then((settings: any) => {
            const raw = (settings && typeof settings === 'object') ? settings : {};
            setPayload(raw);
            const next = Array.isArray(raw.absenceCodes) && raw.absenceCodes.length > 0
                ? raw.absenceCodes.map((c: any) => (typeof c === 'string'
                    ? { code: c, isFullDay: true, autoApply: true, color: '#ffe39b' }
                    : { code: String(c.code || 'ABS'), isFullDay: !!c.isFullDay, autoApply: c.autoApply ?? !!c.isFullDay, color: c.color || '#ffe39b' }))
                : [];
            setCodes(next);
        }).catch((e) => console.error('Failed to load absence codes', e)).finally(() => setLoading(false));
    }, []);

    const addCode = () => {
        const code = newCode.trim().toUpperCase();
        if (!code || codes.some((c) => c.code === code)) return;
        setCodes((prev) => [...prev, { code, isFullDay: true, autoApply: true, color: newColor }]);
        setNewCode('');
    };
    const removeCode = (code: string) => setCodes((prev) => prev.filter((c) => c.code !== code));
    const updateColor = (code: string, color: string) => setCodes((prev) => prev.map((c) => (c.code === code ? { ...c, color } : c)));
    const toggleFullDay = (code: string) => setCodes((prev) => prev.map((c) => (c.code === code ? { ...c, isFullDay: !c.isFullDay } : c)));
    const toggleAutoApply = (code: string) => setCodes((prev) => prev.map((c) => (c.code === code ? { ...c, autoApply: !c.autoApply } : c)));

    const handleSave = async () => {
        setSaving(true);
        try {
            await planningApi.saveSettings({ ...payload, absenceCodes: codes });
            onSaved?.(codes);
            setSaved(true);
            setTimeout(() => setSaved(false), 2000);
        } catch (e) {
            console.error('Failed to save absence codes', e);
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-content-muted" size={24} /></div>;

    return (
        <div className="max-w-lg space-y-4">
            <p className="text-xs text-content-muted italic">CP, RTT, Maladie… Ajoutez vos propres codes utilisés pour les demandes de congé(s) de vos salariés.</p>
            <div className="flex gap-2">
                <input type="color" value={newColor} onChange={(e) => setNewColor(e.target.value)}
                    className="h-9 w-9 rounded-lg border border-border bg-surface-soft p-0.5 cursor-pointer shrink-0" />
                <input value={newCode} onChange={(e) => setNewCode(e.target.value.toUpperCase())} placeholder="Nouveau code (ex: RTT, CP...)"
                    onKeyDown={(e) => e.key === 'Enter' && addCode()}
                    className="flex-1 bg-surface-soft border border-border rounded-xl px-3 py-2 text-sm text-content outline-none focus:border-brand-500" />
                <button onClick={addCode} className="px-4 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg text-xs font-bold flex items-center gap-1.5">
                    <Plus size={14} /> Ajouter
                </button>
            </div>
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {codes.length === 0 ? (
                    <p className="text-center text-content-muted italic text-xs py-8">Aucun code d'absence configuré.</p>
                ) : codes.map((item) => (
                    <div key={item.code} className="flex items-center justify-between p-3 rounded-xl bg-surface-soft border border-border">
                        <div className="flex items-center gap-3">
                            <input type="color" value={item.color || '#ffe39b'} onChange={(e) => updateColor(item.code, e.target.value)}
                                className="w-6 h-6 rounded-lg border-none bg-transparent cursor-pointer" title="Changer la couleur" />
                            <span className="font-black text-sm min-w-[48px]" style={{ color: item.color || '#ffe39b' }}>{item.code}</span>
                            <div className="flex gap-1.5">
                                <button onClick={() => toggleFullDay(item.code)} title="Absence journée complète"
                                    className={`px-2 py-0.5 rounded-md text-[9px] font-black uppercase tracking-tight transition-colors ${item.isFullDay ? 'bg-blue-600 text-white' : 'bg-surface border border-border text-content-muted hover:text-content'}`}>
                                    Journée
                                </button>
                                <button onClick={() => toggleAutoApply(item.code)} title="Appliquer automatiquement"
                                    className={`px-2 py-0.5 rounded-md text-[9px] font-black uppercase tracking-tight transition-colors ${item.autoApply ? 'bg-emerald-600 text-white' : 'bg-surface border border-border text-content-muted hover:text-content'}`}>
                                    Auto
                                </button>
                            </div>
                        </div>
                        <button onClick={() => removeCode(item.code)} className="text-content-muted hover:text-red-400 transition-colors shrink-0">
                            <Trash2 size={14} />
                        </button>
                    </div>
                ))}
            </div>
            <button onClick={handleSave} disabled={saving}
                className="px-5 py-2.5 rounded-xl bg-brand-600 hover:bg-brand-700 text-brand-fg text-sm font-bold disabled:opacity-50">
                {saving ? 'Enregistrement...' : saved ? 'Enregistré ✓' : 'Enregistrer'}
            </button>
        </div>
    );
}
